class InteractiveMap extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details');
    this.drawers = this.querySelectorAll('.interactive-map__drawer');
    this.drawerClose = this.querySelectorAll('.interactive-map__drawer-close');
    this.svgParts = this.querySelectorAll('.interactive-map__map svg path');
    this.dropdown = this.querySelector('.interactive-map__option select');
    this.scrollContainer = this.querySelectorAll('.interactive-map__drawer-list-scroll');
    this.scrollableList = this.querySelectorAll('.interactive-map__drawer-list');
    this.scrollUp = this.querySelectorAll('.js-scroll-up');
    this.scrollDown = this.querySelectorAll('.js-scroll-down');
    this.scrollBar = this.querySelectorAll('.js-scrollbar');
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.handleResize();
    this.customScrollDrag();
    this.addToolTips();
    window.addEventListener('resize', this.handleResize);
    this.drawerClose.forEach(button => button.addEventListener('click', this.closeDrawer.bind(this)));
    this.svgParts.forEach(part => {
      part.addEventListener('click', this.openDrawer.bind(this, part.dataset.region));
    });
    if (this.dropdown) {
      this.dropdown.addEventListener('change', (event) => this.openDrawer(event.target.value));
    }
    if (this.scrollableList.length && this.scrollBar.length) {
      this.scrollableList.forEach((list, idx) => {
        list.addEventListener('scroll', () => this.customScrollBar());
      });
      this.customScrollBar();
    }

    // Add click event listeners to scrollUp and scrollDown
    if (this.scrollUp.length) {
      this.scrollUp.forEach((btn, idx) => {
        btn.addEventListener('click', () => this.customScrollBarUp(idx));
      });
    }
    if (this.scrollDown.length) {
      this.scrollDown.forEach((btn, idx) => {
        btn.addEventListener('click', () => this.customScrollBarDown(idx));
      });
    }
  }

  openDrawer(region) {
    this.closeDrawer();
    if (region) {
      this.drawers.forEach(drawer => {
        if (drawer.dataset.region === region) {
          drawer.classList.add('is-open');
          this.toggleActiveRegion(region);
          if (window.innerWidth < 990) {
            document.body.classList.add('no-scroll');
          }
        }
      });
    }
  }

  toggleActiveRegion(region) {
    this.svgParts.forEach(part => {
      if (part.dataset.region === region) {
        part.classList.add('is-active');
      } else {
        part.classList.remove('is-active');
      }
    });
  }

  closeDrawer() {
    this.drawers.forEach(drawer => drawer.classList.remove('is-open'));
    document.body.classList.remove('no-scroll');
    this.toggleActiveRegion();
  }

  addToolTips() {
    this.svgParts.forEach(part => {
      if (part.dataset.region) {
        const title = document.createElement('title');
        title.textContent = part.dataset.region.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        part.appendChild(title);
      }
    });
  }

  customScrollBar() {
    this.scrollableList.forEach((list, idx) => {
      const bar = this.scrollBar[idx];
      // Calculate thumb height and position as percentage
      const visibleRatio = list.clientHeight / list.scrollHeight;
      const thumbHeightPercent = Math.max(visibleRatio * 100, (30 / list.clientHeight) * 100); // min height as percent
      const scrollRatio = list.scrollTop / (list.scrollHeight - list.clientHeight);
      const thumbTopPercent = scrollRatio * (100 - thumbHeightPercent);
      // Set thumb styles via CSS variable (percentage)
      if (thumbHeightPercent === 100) {
        this.scrollContainer[idx].classList.add('hidden');
      } else {
        this.scrollContainer[idx].classList.remove('hidden');
      }
      bar.style.setProperty('--thumb-height', `${thumbHeightPercent}%`);
      bar.style.setProperty('--thumb-top', `${thumbTopPercent}%`);
    });
  }

  customScrollBarUp(idx) {
    const list = this.scrollableList[idx];
    list.scrollTop -= 100;
    this.customScrollBar();
  }

  customScrollBarDown(idx) {
    const list = this.scrollableList[idx];
    list.scrollTop += 100;
    this.customScrollBar();
  }

  customScrollDrag() {
    this.scrollBar.forEach((bar, idx) => {
      let isDragging = false;
      let startY = 0;
      let startScrollTop = 0;
      // Helper to get thumb height and offset
      const getThumbMetrics = () => {
        const list = this.scrollableList[idx];
        const visibleRatio = list.clientHeight / list.scrollHeight;
        const thumbHeight = Math.max(visibleRatio * list.clientHeight, 30); // px
        return { thumbHeight };
      };
      // Mouse events for drag
      bar.addEventListener('mousedown', (e) => {
        // Only start drag if clicking on the thumb (assume :after covers the thumb area)
        // Get bounding rect and check if click is within thumb
        const rect = bar.getBoundingClientRect();
        const list = this.scrollableList[idx];
        const visibleRatio = list.clientHeight / list.scrollHeight;
        const thumbHeight = Math.max(visibleRatio * list.clientHeight, 30);
        const scrollRatio = list.scrollTop / (list.scrollHeight - list.clientHeight);
        const thumbTop = rect.top + scrollRatio * (rect.height - thumbHeight);
        if (e.clientY >= thumbTop && e.clientY <= thumbTop + thumbHeight) {
          isDragging = true;
          startY = e.clientY;
          startScrollTop = list.scrollTop;
          e.preventDefault();
        }
      });
      document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        const list = this.scrollableList[idx];
        const barRect = bar.getBoundingClientRect();
        const { thumbHeight } = getThumbMetrics();
        const deltaY = e.clientY - startY;
        const scrollableHeight = list.scrollHeight - list.clientHeight;
        const barHeight = barRect.height;
        const maxThumbTop = barHeight - thumbHeight;
        let thumbTop = (list.scrollTop / scrollableHeight) * maxThumbTop + deltaY;
        thumbTop = Math.max(0, Math.min(thumbTop, maxThumbTop));
        const newScrollTop = (thumbTop / maxThumbTop) * scrollableHeight;
        list.scrollTop = newScrollTop;
        this.customScrollBar();
      });
      document.addEventListener('mouseup', () => {
        if (isDragging) {
          isDragging = false;
        }
      });
    });
  }

  handleResize() {
    if (window.innerWidth >= 990) {
      this.details.open = true;
      document.body.classList.remove('no-scroll');
    }
  }
}

customElements.define('interactive-map', InteractiveMap);